import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Hero from '../components/Hero';
import CategoryFilter from '../components/CategoryFilter';
import StoreCard from '../components/marketplace/StoreCard';
import StoreDetail from '../components/marketplace/StoreDetail';
import { craftsmanService } from '../services/craftsmanService';
import { Loader2 } from 'lucide-react';

export default function CraftiesMarketplace() {
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const [stores, setStores] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const searchQuery = searchParams.get('search') || '';
  const locationQuery = searchParams.get('location') || '';
  
  useEffect(() => {
    const loadStores = async () => {
      try {
        setLoading(true);
        const storesData = await craftsmanService.getAllCraftsmen();
        console.log('Loaded craftsmen:', storesData);
        setStores(storesData || []);
      } catch (error) {
        console.error('Error loading craftsmen:', error);
        setError(error instanceof Error ? error.message : 'Failed to load craftsmen');
      } finally {
        setLoading(false);
      }
    };
    
    loadStores();
  }, []);

  const filteredStores = React.useMemo(() => {
    const normalizedSearch = searchQuery.toLowerCase().trim();
    
    return stores.filter(store => {
      // Check category first
      const matchesCategory = selectedCategory === 'All' || 
                            store.craft_professions?.category === selectedCategory;

      if (!matchesCategory) {
        return false;
      }

      // If no search query, return category match
      if (!normalizedSearch) {
        return true;
      }

      // Normalize all searchable fields
      const searchableFields = [
        store.store_name,
        store.owner_name,
        store.craft_professions?.name,
        store.craft_professions?.category,
        store.description,
        store.location
      ].filter(Boolean);

      // Try exact matches first
      const exactMatch = searchableFields.some(field => 
        field?.toLowerCase().includes(normalizedSearch)
      );

      if (exactMatch) {
        return true;
      }

      // Try word-by-word matching
      const searchWords = normalizedSearch.split(/\s+/);
      const matchesAllWords = searchWords.every(word => 
        searchableFields.some(field => 
          field?.toLowerCase().includes(word)
        )
      );

      return matchesAllWords;
    });
  }, [stores, selectedCategory, searchQuery]);

  const selectedStore = selectedStoreId 
    ? stores.find(t => t.id === selectedStoreId)
    : null;

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Hero />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (selectedStore) {
    return (
      <StoreDetail 
        store={selectedStore} 
        onBack={() => setSelectedStoreId(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Hero />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
        <div className="mb-8">
          <CategoryFilter 
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
          />
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
              {filteredStores.map((store) => (
                <StoreCard
                  key={store.id}
                  id={store.id.toString()}
                  name={store.store_name}
                  category={store.craft_professions?.category || 'Uncategorized'}
                  rating={store.rating || 0}
                  reviews={store.reviews || 0}
                  location={store.location}
                  image={store.avatar_url || 'https://api.dicebear.com/7.x/initials/svg?seed=' + store.store_name}
                  verified={store.verified || false}
                  description={store.description}
                  featured={[store.craft_professions?.name || 'Artisan']}
                  website={store.website || ''}
                  onClick={() => setSelectedStoreId(store.id)}
                />
              ))}
            </div>

            {filteredStores.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">
                  {stores.length === 0 ? (
                    "No craftsmen available yet. Check back soon!"
                  ) : (
                    "No craftsmen found matching your search criteria."
                  )}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
